import React from 'react';
import Heading from 'components/Heading';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import FieldRow from 'components/FormElements/FieldRow';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import Spacing from 'components/Spacing';
import { backgroundExports as rules } from 'further-ui/validations';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { BackgroundExportTypes } from 'further-types/background-exports';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import MultiFirmSelector from 'components/FormElements/MultiFirmSelector';

const todaysDate = new Date();

const BillingPage: React.FC = () => {
  type FormValues = {
    firmIds: Array<string>;
    date: Date;
  };

  const formMethods = useForm<FormValues>({
    shouldFocusError: true,
    resolver: zodResolver(rules.CreateBackgroundExportInput),
    values: {
      firmIds: [],
      date: todaysDate,
    },
    criteriaMode: 'all',
    reValidateMode: 'onSubmit',
  });

  const { control, watch } = formMethods;
  const { firmIds, date } = watch();

  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleExport = () => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.BillingExport,
      params: {
        date,
        firmIds,
      },
    });
  };

  const breadcrumbs = [{ label: 'Dashboard' }];

  return (
    <PageContainer heading="Billing" breadcrumbs={breadcrumbs}>
      <PageContent withSpacing>
        <Heading variant="h2" noMargin>
          Billing
        </Heading>

        <form>
          <Spacing>
            <FieldRow title="Firm" centerTitle>
              <Controller
                control={control}
                name="firmIds"
                render={({ field, fieldState }) => (
                  <MultiFirmSelector
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </FieldRow>

            <FieldRow title="Date" centerTitle>
              <Controller
                control={control}
                name="date"
                render={({ field, fieldState: { error } }) => (
                  <AppDatePicker
                    required
                    value={field.value}
                    disableFuture
                    placeholder="Select Date"
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FieldRow>
            <ButtonGroup>
              <Button
                variant="contained"
                color="primary"
                loading={isExporting}
                disabled={!firmIds || firmIds.length === 0 || !date}
                onClick={handleExport}
              >
                Export
              </Button>
            </ButtonGroup>
          </Spacing>
        </form>
      </PageContent>
    </PageContainer>
  );
};

export default BillingPage;
